/**
 * Company contract theme (ClickUp 869f858n0): palettes, colour rules and the
 * shape the API returns. The API validates the same rules; these are for the
 * editor's live feedback.
 */
export interface CompanyTheme {
  /** API path (`/v1/...?v=hash`) that serves the logo, or null. */
  logoUrl: string | null;
  logoWidth: number | null;
  logoHeight: number | null;
  headingColor: string;
  textColor: string;
  accentColor: string;
  paletteKey: string | null;
  /** True when nothing is saved and Etappio's standard applies. */
  isDefault: boolean;
  companyName: string;
}

export type ThemeColorField = 'headingColor' | 'textColor' | 'accentColor';
export type ThemeColors = Record<ThemeColorField, string>;

export const THEME_COLOR_FIELDS: ThemeColorField[] = ['headingColor', 'textColor', 'accentColor'];

/**
 * Recommended contrast against white (WCAG AA): body text and accent 4.5:1,
 * headings 3:1. A recommendation, not a rule – the company may save its own
 * colour (ClickUp 869fa66kk); the editor suggests a darker variant.
 */
export const MIN_CONTRAST: Record<ThemeColorField, number> = {
  headingColor: 3,
  textColor: 4.5,
  accentColor: 4.5,
};

export interface ThemePalette extends ThemeColors {
  key: string;
}

export const DEFAULT_PALETTE_KEY = 'etappio';

/** Ready-made palettes; every one meets the contrast rules (see the test). */
export const THEME_PALETTES: ThemePalette[] = [
  { key: 'etappio', headingColor: '#1E3A5F', textColor: '#1F2A37', accentColor: '#0F6F7D' },
  { key: 'grafit', headingColor: '#2B2F36', textColor: '#1F2328', accentColor: '#4A5563' },
  { key: 'skog', headingColor: '#1F4D3A', textColor: '#1E2B24', accentColor: '#2F6B4F' },
  { key: 'havsbla', headingColor: '#0B3D6B', textColor: '#1B2733', accentColor: '#0B6E99' },
  { key: 'vinrod', headingColor: '#6B1D2A', textColor: '#2A1E20', accentColor: '#8E2C3B' },
  { key: 'terrakotta', headingColor: '#8A3B1F', textColor: '#2B211C', accentColor: '#A4471F' },
];

export const DEFAULT_THEME_COLORS: ThemeColors = {
  headingColor: THEME_PALETTES[0].headingColor,
  textColor: THEME_PALETTES[0].textColor,
  accentColor: THEME_PALETTES[0].accentColor,
};

export const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export const isHexColor = (value: string) => HEX_COLOR_PATTERN.test(value);

const channel = (value: number) => {
  const srgb = value / 255;
  return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
};

/** WCAG 2.x contrast ratio of a #RRGGBB colour against white. */
export function contrastAgainstWhite(hex: string): number {
  const red = parseInt(hex.slice(1, 3), 16);
  const green = parseInt(hex.slice(3, 5), 16);
  const blue = parseInt(hex.slice(5, 7), 16);
  const luminance = 0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue);
  return 1.05 / (luminance + 0.05);
}

export const meetsContrast = (field: ThemeColorField, hex: string) =>
  isHexColor(hex) && contrastAgainstWhite(hex) >= MIN_CONTRAST[field];

const toHex = (value: number) => Math.round(Math.max(0, Math.min(255, value))).toString(16).padStart(2, '0');

/**
 * The same colour made darker until it reaches `minimum` against white: hue
 * and saturation stay (HSL), only the lightness is lowered, so the brand is
 * still recognisable. Black is the last resort and always passes.
 */
export function suggestReadableColor(hex: string, minimum: number): string {
  if (!isHexColor(hex)) return '#000000';
  if (contrastAgainstWhite(hex) >= minimum) return hex.toUpperCase();
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let lightness = (max + min) / 2;
  const delta = max - min;
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
  let hue = 0;
  if (delta !== 0) {
    if (max === r) hue = ((g - b) / delta) % 6;
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
  }
  hue *= 60;
  const fromHsl = (l: number) => {
    const chroma = (1 - Math.abs(2 * l - 1)) * saturation;
    const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
    const m = l - chroma / 2;
    const [r1, g1, b1] =
      hue < 60 ? [chroma, x, 0] : hue < 120 ? [x, chroma, 0] : hue < 180 ? [0, chroma, x]
        : hue < 240 ? [0, x, chroma] : hue < 300 ? [x, 0, chroma] : [chroma, 0, x];
    return `#${toHex((r1 + m) * 255)}${toHex((g1 + m) * 255)}${toHex((b1 + m) * 255)}`.toUpperCase();
  };
  while (lightness > 0) {
    lightness = Math.max(0, lightness - 0.01);
    const candidate = fromHsl(lightness);
    if (contrastAgainstWhite(candidate) >= minimum) return candidate;
  }
  return '#000000';
}

/** The palette whose colours equal these, if any. */
export const paletteFor = (colors: ThemeColors) =>
  THEME_PALETTES.find(palette =>
    THEME_COLOR_FIELDS.every(field => palette[field].toUpperCase() === colors[field].toUpperCase())
  ) ?? null;

/** Logo rules, mirrored from the API for an early, friendly message. */
export const LOGO_ACCEPT = 'image/png,image/jpeg,.png,.jpg,.jpeg';
export const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;

export const LOGO_ERROR_CODES = ['LOGO_UNSUPPORTED_TYPE', 'LOGO_TOO_LARGE', 'LOGO_DIMENSIONS'] as const;
